import { useDeleteUser } from "@vidario/api-client";
import { type User } from "@vidario/domain";
import { Button, Input, Modal, useToast } from "@vidario/ui";
import { AlertTriangle } from "lucide-react";
import { useState, type ReactElement } from "react";

export interface DeleteUserDialogProps {
  user: User | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Super-admin only: confirm-then-delete. The "type the full name" guard
 * is intentional — the action is irreversible (DSGVO-shaped hard delete,
 * jobs reassigned to the actor), and a stray click on a kebab menu must
 * not be enough to nuke a recruiter. */
export function DeleteUserDialog({ user, open, onOpenChange }: DeleteUserDialogProps): ReactElement {
  const toast = useToast();
  const deleteUser = useDeleteUser();
  const [confirmName, setConfirmName] = useState("");

  const closeAndReset = (next: boolean): void => {
    if (!next) setConfirmName("");
    onOpenChange(next);
  };

  const expected = user?.name.trim() ?? "";
  const canDelete = expected.length > 0 && confirmName.trim() === expected;

  const submit = async (): Promise<void> => {
    if (!user || !canDelete) return;
    try {
      const result = await deleteUser.mutateAsync(user.id);
      toast.push({
        kind: "success",
        title: "Nutzer gelöscht",
        message:
          result.reassignedJobCount > 0
            ? `${user.name} wurde entfernt. ${result.reassignedJobCount} ${
                result.reassignedJobCount === 1 ? "Stelle wurde" : "Stellen wurden"
              } auf Sie übertragen.`
            : `${user.name} wurde entfernt.`,
      });
      closeAndReset(false);
    } catch (err) {
      toast.push({
        kind: "error",
        title: "Löschen fehlgeschlagen",
        message: err instanceof Error ? err.message : "Unbekannter Fehler.",
      });
    }
  };

  return (
    <Modal
      open={open}
      onOpenChange={closeAndReset}
      title="Nutzer löschen"
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={() => closeAndReset(false)}>
            Abbrechen
          </Button>
          <Button
            variant="destructive"
            disabled={!canDelete}
            loading={deleteUser.isPending}
            onClick={() => void submit()}
          >
            Endgültig löschen
          </Button>
        </>
      }
    >
      {user && (
        <>
          <div className="mb-4 flex gap-2 rounded-md bg-danger-50 p-3 text-sm text-danger-700">
            <AlertTriangle size={16} className="mt-0.5 shrink-0" />
            <div>
              <div className="font-medium">
                {user.name} wird unwiderruflich entfernt.
              </div>
              <div className="mt-1 text-xs">
                Die Stellen dieses Nutzers werden auf Sie übertragen. Notizen
                und Audit-Log-Einträge bleiben erhalten, der Autor wird dort
                als &bdquo;gel&ouml;schter Nutzer&ldquo; angezeigt.
              </div>
            </div>
          </div>
          <Input
            label={`Zur Bestätigung „${expected}" eintippen`}
            value={confirmName}
            onChange={(e) => setConfirmName(e.target.value)}
            placeholder={expected}
            data-testid="delete-user-confirm-input"
          />
        </>
      )}
    </Modal>
  );
}
